import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, X, Filter, Calendar } from 'lucide-react';
import api from '../services/api';

const FormPage = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formDetail, setFormDetail] = useState(null);
  const [formResponse, setFormResponse] = useState({});
  const [responsesList, setResponsesList] = useState([]);
  const [filteredResponsesList, setFilteredResponsesList] = useState([]);
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [uploadingImages, setUploadingImages] = useState({});
  const [selectedImages, setSelectedImages] = useState({});
  const [subOptions, setSubOptions] = useState({});
  
  const [selectedPeriodFilter, setSelectedPeriodFilter] = useState('');
  const [availablePeriods, setAvailablePeriods] = useState([]);
  
  const userRole = localStorage.getItem('userRole') || '';
  const isDepartment = userRole === 'department';

const generateMonthYearOptions = () => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  const currentYear = now.getFullYear();
  
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const options = [];
  
  // Generate Nov, Dec, Jan options
// Generate last 3 months dynamically
for (let i = 3; i >= 1; i--) {
  let month = currentMonth - i;
  let year = currentYear;

  if (month < 0) {
    month += 12;
    year -= 1;
  }

  const yearShort = String(year).slice(-2);
  const monthNumber = String(month + 1).padStart(2, '0');
  const value = `${yearShort}${monthNumber}`;
  const label = `${monthNames[month]} ${year}`;

  options.push({ value, label });
}
  
  return options;
};

  const monthYearOptions = generateMonthYearOptions();

  useEffect(() => {
    if (responsesList && responsesList.length > 0) {
      const periods = [...new Set(responsesList.map(item => item.type))].filter(Boolean);
      setAvailablePeriods(periods.sort().reverse());
    } else {
      setAvailablePeriods([]);
    }
  }, [responsesList]);

  useEffect(() => {
    if (availablePeriods.length > 0 && selectedPeriodFilter === '') {
      // Generate previous month value
      const now = new Date();
      const currentMonth = now.getMonth();
      let prevMonth = currentMonth - 1;
      let prevYear = now.getFullYear();
      
      if (prevMonth < 0) {
        prevMonth = 11;
        prevYear = prevYear - 1;
      }
      
      const yearShort = String(prevYear).slice(-2);
      const monthNumber = String(prevMonth + 1).padStart(2, '0');
      const previousMonthValue = `${yearShort}${monthNumber}`;
      
      // Check if previous month exists in available periods
      if (availablePeriods.includes(previousMonthValue)) {
        setSelectedPeriodFilter(previousMonthValue);
      } else {
        // If previous month doesn't exist, set to 'all'
        setSelectedPeriodFilter('all');
      }
    }
  }, [availablePeriods]);

  useEffect(() => {
    if (selectedPeriodFilter === 'all' || selectedPeriodFilter === '') {
      setFilteredResponsesList(responsesList);
    } else {
      const filtered = responsesList.filter(item => item.type === selectedPeriodFilter);
      setFilteredResponsesList(filtered);
    }
  }, [selectedPeriodFilter, responsesList]);

  useEffect(() => {
    setResponsesList([]);
    setLoading(true);
    
    fetchFormDetail();
    fetchResponsesList();
    
    const options = generateMonthYearOptions();
    if (options.length > 0) {
      setSelectedMonthYear(options[0].value);
    }
  }, [formId]);

  const fetchFormDetail = async () => {
    setError(null);
    try {
      const response = await fetch(`${api.BASE_URL}/form/detail/${formId}`, {
        headers: {
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (!result.error && result.data) {
          setFormDetail(result.data);
          
          const subOptionsMap = {};
          if (result.data.fields && Array.isArray(result.data.fields)) {
            result.data.fields.forEach(field => {
              if (field.type === 'sub-2' && field.value && typeof field.value === 'object') {
                subOptionsMap[field.name] = field.value;
              }
            });
          }
          setSubOptions(subOptionsMap);
          
          if (!isDepartment) {
            const initialResponse = {};
            if (result.data.fields && Array.isArray(result.data.fields)) {
              result.data.fields.forEach((field, fieldIndex) => {
                if (field.type === 'table') {
                  const tableKey = `table_${fieldIndex}`;
                  const rows = field.rows || [];
                  initialResponse[tableKey] = rows.map(row => ({ ...row }));
                } else {
                  const fieldLabel = field.label || field.name;
                  if (field.type === 'number' || field.type === 'currency') {
                    initialResponse[fieldLabel] = '';
                  } else if (field.type === 'date') {
                    initialResponse[fieldLabel] = '';
                  } else if (field.type === 'image') {
                    initialResponse[fieldLabel] = '';
                  } else if (field.type === 'list' && field.value && field.value.length > 0) {
                    initialResponse[fieldLabel] = field.value[0];
                  } else {
                    initialResponse[fieldLabel] = '';
                  }
                }
              });
            }
            setFormResponse(initialResponse);
          }
        } else {
          setError(result.message || 'Failed to fetch form details');
        }
      } else {
        setError('Failed to fetch form details');
      }
    } catch (error) {
      console.error('Error fetching form details:', error);
      setError('An error occurred while fetching form details');
    }
  };

  const fetchResponsesList = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${api.BASE_URL}/form/${formId}/response`, {
        headers: {
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (!result.error && result.data) {
          setResponsesList(Array.isArray(result.data) ? result.data : [result.data]);
        } else if (result.message === 'Response Not Found') {
          setResponsesList([]);
        } else {
          setError(result.message || 'Failed to fetch responses');
        }
      } else {
        setError('Failed to fetch responses');
      }
    } catch (error) {
      console.error('Error fetching responses:', error);
      setError('An error occurred while fetching responses');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldLabel, value, fieldType, field = null) => {
    setFormResponse(prev => {
      const newResponse = {
        ...prev,
        [fieldLabel]: fieldType === 'number' || fieldType === 'currency' ? (value === '' ? '' : Number(value)) : value
      };

      if (field && formDetail?.fields) {
        formDetail.fields.forEach(otherField => {
          if (otherField.type === 'sub-2' && subOptions[otherField.name]) {
            const dependentFieldName = findDependentField(otherField, field);
            if (dependentFieldName === fieldLabel) {
              newResponse[otherField.label || otherField.name] = '';
            }
          }
        });
      }

      return newResponse;
    });
  };

  const handleTableCellChange = (tableKey, rowIndex, columnKey, value) => {
    setFormResponse(prev => {
      const currentTableData = prev[tableKey] || [];
      const tableData = JSON.parse(JSON.stringify(currentTableData)); // Deep clone
      
      if (!tableData[rowIndex]) {
        tableData[rowIndex] = {};
      }
      
      tableData[rowIndex][columnKey] = value;
      
      return {
        ...prev,
        [tableKey]: tableData
      };
    });
  };

  const findDependentField = (subField, changedField) => {
    if (!subOptions[subField.name]) return null;
    
    const changedFieldLabel = changedField.label || changedField.name;
    const currentValue = formResponse[changedFieldLabel];
    
    if (currentValue && subOptions[subField.name][currentValue]) {
      return changedFieldLabel;
    }
    
    return null;
  };

  const getSubOptionsForField = (field) => {
    if (!field || field.type !== 'sub-2' || !subOptions[field.name]) {
      return [];
    }

    const parentField = findParentField(field);
    if (!parentField) return [];

    const parentValue = formResponse[parentField.label || parentField.name];
    if (!parentValue || !subOptions[field.name][parentValue]) {
      return [];
    }

    return subOptions[field.name][parentValue];
  };

  const findParentField = (subField) => {
    if (!formDetail?.fields || !subOptions[subField.name]) return null;

    for (const field of formDetail.fields) {
      if (field.type === 'list' && field.value) {
        const fieldValue = formResponse[field.label || field.name];
        if (fieldValue && subOptions[subField.name][fieldValue]) {
          return field;
        }
      }
    }
    return null;
  };

  const handleImageSelect = async (fieldLabel, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select only image files');
      return;
    }

    setSelectedImages(prev => ({ ...prev, [fieldLabel]: file }));
    setError(null);
    
    await uploadImage(fieldLabel, file);
  };

  const uploadImage = async (fieldLabel, file) => {
    try {
      setUploadingImages(prev => ({ ...prev, [fieldLabel]: true }));

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${api.BASE_URL}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('authToken')
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        
        if (!result.error && result.file) {
          setFormResponse(prev => ({
            ...prev,
            [fieldLabel]: result.file
          }));
        } else {
          setError(result.message || 'Failed to upload image');
        }
      } else {
        setError('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('An error occurred while uploading image');
    } finally {
      setUploadingImages(prev => ({ ...prev, [fieldLabel]: false }));
    }
  };

  const removeImage = (fieldLabel) => {
    setSelectedImages(prev => {
      const newImages = { ...prev };
      delete newImages[fieldLabel];
      return newImages;
    });
    
    setFormResponse(prev => ({
      ...prev,
      [fieldLabel]: ''
    }));
  };

  const submitFormResponse = async () => {
    if (!selectedMonthYear) {
      setError('Please select month and year');
      return;
    }

    setSubmitLoading(true);
    setError(null);

    try {
      const responseData = { ...formResponse };
      
      const payload = {
        formId: parseInt(formId),
        type: selectedMonthYear,
        response: responseData
      };

      const response = await fetch(`${api.BASE_URL}/form/${formId}/response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken')
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        if (!result.error) {
          setSubmitSuccess(true);
          setShowForm(false);
          await fetchResponsesList();
          
          setTimeout(() => {
            setSubmitSuccess(false);
          }, 5000);
        } else {
          setError(result.message || 'Failed to submit form');
        }
      } else {
        setError('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('An error occurred while submitting form');
    } finally {
      setSubmitLoading(false);
    }
  };

  const downloadExcel = () => {
    try {
      const csvData = [];
      
      const headers = ['Period', 'Institute'];
      formDetail?.fields?.forEach((field, fieldIndex) => {
        if (field.type === 'table') {
          const tableHeaders = field.header || {};
          const headerKeys = Object.keys(tableHeaders).sort();
          headerKeys
            .filter((key) => {
              // For department users, skip columns 1 (Training Name) and 3 (Clientele)
              if (isDepartment && (key === '1' || key === '2' || key === '3')) {
                return false;
              }
              return true;
            })
            .forEach(key => {
              headers.push(tableHeaders[key]);
            });
        } else {
          headers.push(field.label || field.name);
        }
      });
      csvData.push(headers);
      
      filteredResponsesList.forEach(item => {
        const row = [
          formatTypeDisplay(item.type),
          item.inst || ''
        ];
        
        formDetail?.fields?.forEach((field, fieldIndex) => {
          if (field.type === 'table') {
            const tableKey = `table_${fieldIndex}`;
            const tableData = item.response?.[tableKey] || [];
            const tableHeaders = field.header || {};
            const headerKeys = Object.keys(tableHeaders).sort();
            
            headerKeys
              .filter((key) => {
                // For department users, skip columns 1 (Training Name) and 3 (Clientele)
                if (isDepartment && (key === '1' || key === '2' || key === '3')) {
                  return false;
                }
                return true;
              })
              .forEach(key => {
                const columnData = tableData.map(row => row[key] || '').join('\n');
                row.push(columnData);
              });
          } else {
            const fieldLabel = field.label || field.name;
            const cellValue = item.response?.[fieldLabel];
            
            if (field.type === 'image' && cellValue) {
              row.push('Image attached');
            } else {
              row.push(cellValue || '');
            }
          }
        });
        
        csvData.push(row);
      });
      
      const csvContent = csvData.map(row => 
        row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${formDetail?.name || 'form'}_responses.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download Excel file');
    }
  };

  const renderFormField = (field, fieldIndex) => {
    if (field.type === 'table') {
      const tableKey = `table_${fieldIndex}`;
      const tableData = formResponse[tableKey] || field.rows || [];
      const headers = field.header || {};
      const headerKeys = Object.keys(headers).sort();

      return (
        <div className="w-full overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                {headerKeys.map((key) => (
                  <th key={key} className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">
                    {headers[key]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50">
                {headerKeys.map((colKey) => {
  // Define which columns should be numeric (columns 4, 5, 6, 7)
  const isNumericColumn = ['4', '5', '6', '7'].includes(colKey);
  
  return (
    <td key={colKey} className="border border-gray-300 px-4 py-3" style={{ minWidth: '500px' }}>
      {field.rows && field.rows[rowIndex] && field.rows[rowIndex][colKey] && field.rows[rowIndex][colKey].trim() !== '' ? (
        <div className="px-3 py-2 text-base whitespace-pre-wrap leading-relaxed">
          {field.rows[rowIndex][colKey]}
        </div>
      ) : (
        isNumericColumn ? (
          <input
            type="number"
            value={row[colKey] || ''}
            onChange={(e) => handleTableCellChange(tableKey, rowIndex, colKey, e.target.value)}
            className="w-full border-0 focus:ring-2 focus:ring-blue-500 rounded px-3 py-2 text-base"
            placeholder={`Enter ${headers[colKey]}`}
            min="0"
            step="1"
          />
        ) : (
          <textarea
            value={row[colKey] || ''}
            onChange={(e) => handleTableCellChange(tableKey, rowIndex, colKey, e.target.value)}
            className="w-full min-h-[100px] border-0 focus:ring-2 focus:ring-blue-500 rounded px-3 py-2 text-base resize-y leading-relaxed"
            placeholder={`Enter ${headers[colKey]}`}
            rows="5"
          />
        )
      )}
    </td>
  );
})}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    const fieldLabel = field.label || field.name;
    const fieldValue = formResponse[fieldLabel] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={fieldValue}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, field.type, field)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={field.info || `Enter ${fieldLabel.toLowerCase()}`}
            required={field.required}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={fieldValue}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, field.type, field)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required={field.required}
          />
        );

      case 'number':
      case 'currency':
        return (
          <input
            type="number"
            value={fieldValue}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, field.type, field)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={field.type === 'currency' ? 'Enter amount' : 'Enter number'}
            required={field.required}
            min="0"
            step={field.type === 'currency' ? '0.01' : '1'}
          />
        );

      case 'list':
        return (
          <select
            value={fieldValue}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, field.type, field)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required={field.required}
          >
            <option value="">Select {fieldLabel}</option>
            {field.value && field.value.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'sub-2':
        const availableSubOptions = getSubOptionsForField(field);
        const parentField = findParentField(field);
        const isDisabled = !parentField || !formResponse[parentField?.label || parentField?.name] || availableSubOptions.length === 0;

        return (
          <select
            value={fieldValue}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, field.type, field)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            required={field.required}
            disabled={isDisabled}
          >
            <option value="">
              {isDisabled ? `First select ${parentField?.label || 'parent option'}` : `Select ${fieldLabel}`}
            </option>
            {availableSubOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'image':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {uploadingImages[fieldLabel] ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mb-2 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> image
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, JPEG (MAX. 5MB)</p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => handleImageSelect(fieldLabel, e)}
                  disabled={uploadingImages[fieldLabel]}
                />
              </label>
            </div>

            {(selectedImages[fieldLabel] || fieldValue) && (
              <div className="flex items-center justify-between p-2 bg-blue-50 rounded border">
                <span className="text-sm text-blue-700 truncate">
                  {selectedImages[fieldLabel]?.name || fieldValue || 'Image uploaded'}
                </span>
                <button
                  type="button"
                  onClick={() => removeImage(fieldLabel)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={fieldValue}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value, field.type, field)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={field.info || `Enter ${fieldLabel.toLowerCase()}`}
            required={field.required}
          />
        );
    }
  };

  const formatTypeDisplay = (type) => {
    if (!type) return '';
    if (type.length === 4) {
      const year = '20' + type.slice(0, 2);
      const month = type.slice(2, 4);
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      const monthName = monthNames[parseInt(month) - 1] || month;
      return `${monthName} ${year}`;
    }
    return type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!formDetail) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Form not found</h2>
        <p className="text-gray-600">The requested form could not be found or you don't have access to it.</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                {formDetail.name}
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                Type: <span className="font-medium">{formDetail.type}</span>
              </p>
            </div>
            
            <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
              {!isDepartment && (
                <button
                  onClick={() => setShowForm(true)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors duration-200 shadow-sm"
                >
                  <Calendar className="inline-block w-4 h-4 mr-2" />
                  Fill Form
                </button>
              )}
              
              {isDepartment && responsesList.length > 0 && (
                <button
                  onClick={downloadExcel}
                  className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium transition-colors duration-200 shadow-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Report
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {submitSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md">
          <p className="flex items-center">
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            Form submitted successfully!
          </p>
        </div>
      )}

      {!isDepartment && showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Fill Form</h3>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Month & Year <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedMonthYear}
                onChange={(e) => setSelectedMonthYear(e.target.value)}
                className="w-full md:w-64 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {monthYearOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-6">
              {formDetail?.fields && formDetail.fields.map((field, index) => (
                <div key={index} className={field.type === 'image' || field.type === 'table' ? 'col-span-full' : ''}>
                  {field.type !== 'table' && (
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label || field.name}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                  )}
                  {renderFormField(field, index)}
                </div>
              ))}
            </div>

            <div className="pt-4 flex gap-3 border-t">
              <button
                onClick={submitFormResponse}
                disabled={submitLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center font-medium"
              >
                {submitLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
              
              <button
                onClick={() => setShowForm(false)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isDepartment && responsesList.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <Filter className="h-5 w-5 text-gray-600" />
            <label className="text-sm font-medium text-gray-700">
              Filter by Time Period:
            </label>
            <select
              value={selectedPeriodFilter}
              onChange={(e) => setSelectedPeriodFilter(e.target.value)}
              className="flex-1 md:flex-initial md:min-w-[200px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Periods ({responsesList.length})</option>
              {availablePeriods.map((period) => {
                const count = responsesList.filter(item => item.type === period).length;
                return (
                  <option key={period} value={period}>
                    {formatTypeDisplay(period)} ({count})
                  </option>
                );
              })}
            </select>
            {selectedPeriodFilter !== 'all' && (
              <button
                onClick={() => setSelectedPeriodFilter('all')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear Filter
              </button>
            )}
          </div>
          {selectedPeriodFilter !== 'all' && (
            <div className="mt-2 text-sm text-gray-600">
              Showing {filteredResponsesList.length} of {responsesList.length} responses
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-700">Submitted Responses</h3>
        </div>
        
        {filteredResponsesList.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap">
                    Period
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap">
                    Institute
                  </th>
                  {formDetail?.fields?.map((field, fieldIndex) => {
                    if (field.type === 'table') {
                      const headers = field.header || {};
                      const headerKeys = Object.keys(headers).sort();
                      return headerKeys
                        .filter((key) => {
                          // For department users, skip columns 1 (Training Name) and 3 (Clientele)
                          if (isDepartment && (key === '1' || key === '2' || key === '3')) {
                            return false;
                          }
                          return true;
                        })
                        .map((key) => (
                          <th key={`${fieldIndex}_${key}`} className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap">
                            {headers[key]}
                          </th>
                        ));
                    } else {
                      return (
                        <th key={fieldIndex} className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap">
                          {field.label || field.name}
                        </th>
                      );
                    }
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResponsesList.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200 whitespace-nowrap">
                      {formatTypeDisplay(item.type)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200 whitespace-nowrap">
                      {item.inst || '-'}
                    </td>
                    {formDetail?.fields?.map((field, fieldIndex) => {
                      if (field.type === 'table') {
                        const tableKey = `table_${fieldIndex}`;
                        const tableData = item.response?.[tableKey] || [];
                        const headers = field.header || {};
                        const headerKeys = Object.keys(headers).sort();
                        
                        return headerKeys
                          .filter((key) => {
                            // For department users, skip columns 1 (Training Name) and 3 (Clientele)
                            if (isDepartment && (key === '1' || key === '2' || key === '3')) {
                              return false;
                            }
                            return true;
                          })
                          .map((key) => (
                            <td key={`${fieldIndex}_${key}`} className="px-4 py-3 text-base text-gray-900 border-r border-gray-200" style={{ minWidth: '500px' }}>
                              <div className="whitespace-pre-wrap leading-relaxed">
                                {tableData.map((row, rowIdx) => (
                                  <div key={rowIdx} className="mb-2 last:mb-0">{row[key] || '-'}</div>
                                ))}
                              </div>
                            </td>
                          ));
                      } else {
                        const fieldLabel = field.label || field.name;
                        const isImageField = field.type === 'image';
                        const cellValue = item.response?.[fieldLabel] || '';
                        
                        return (
                          <td key={fieldIndex} className="px-4 py-3 text-base text-gray-900 border-r border-gray-200">
                            {cellValue ? (
                              isImageField ? (
                                <a 
                                  href={`https://crd-training.in/uploads/${cellValue}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  View Image
                                </a>
                              ) : (
                                <div className="whitespace-pre-wrap leading-relaxed">{cellValue}</div>
                              )
                            ) : '-'}
                          </td>
                        );
                      }
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg font-medium">
              {selectedPeriodFilter !== 'all' ? 'No responses found for selected period' : 'No responses yet'}
            </p>
            <p className="text-sm mt-1">
              {selectedPeriodFilter !== 'all' 
                ? 'Try selecting a different time period or clear the filter.' 
                : 'There are no submitted responses for this form.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormPage;